import useUser from 'queries/useUser'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import Tippy from '@tippyjs/react'
import { DocumentDuplicateIcon } from '@heroicons/react/outline'
import Button from 'components/ui/Button'
import ButtonLink from 'components/ui/ButtonLink'
import { ShareIcon } from '@heroicons/react/solid'
import FacebookIcon from 'components/icons/FacebookSquareIcon'
import TwitterIcon from 'components/icons/TwitterSquareIcon'
import apiUrl from 'utils/apiUrl'

type Props = {
  lotUrl: string
  lotName: string
  lotEnabled: boolean
  lotId: number
  referralBonus: {
    cents: number
    formatted: string
  }
}

function initializeFacebookAPI() {
  return new Promise((resolve) => {
    // eslint-disable-next-line
    ;(window as any).fbAsyncInit = function () {
      // eslint-disable-next-line
      ;(window as any).FB.init({
        appId: '1835510576699587',
        xfbml: true,
        version: 'v2.7'
      })
      resolve(true)
    }
    ;(function init(d, s, id) {
      const fjs = d.getElementsByTagName(s)[0]
      if (d.getElementById(id)) {
        return
      }
      const js = d.createElement(s) as HTMLScriptElement
      js.id = id
      js.src = '//connect.facebook.net/en_US/sdk.js'
      fjs!.parentNode!.insertBefore(js, fjs)
    })(document, 'script', 'facebook-jssdk')
  })
}

export default function LotReferralSection({
  lotUrl,
  lotEnabled,
  lotId,
  lotName,
  referralBonus
}: Props) {
  const [render, setRender] = useState<boolean>(false)

  useEffect(() => {
    setRender(true)
  }, [])
  const title = `Food trucks at ${lotName}`
  const text = `Check out food trucks serving delicious food at ${lotName}`
  const { user } = useUser({ skip: typeof window === 'undefined' })
  const router = useRouter()
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const { r: codeFromParams } = router.query as { r?: string }

  const referralCode = (codeFromParams || user?.referralCode)?.toLowerCase()

  if (!referralCode) return null
  if (!lotEnabled) return null
  if (referralBonus.cents <= 0) return null

  const referralUrl = `${lotUrl}?r=${referralCode}`.replace(
    /http(s)?:\/\//gi,
    ''
  )

  const trackShareClick = async ({ method }: { method: string }) => {
    const response = await fetch(`${apiUrl()}/track-referral-share.json`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        lot_id: lotId,
        source_url: window.location.href,
        referral_url: referralUrl,
        method
      })
    })
    // eslint-disable-next-line no-console
    console.log(response.status)
  }

  const onCopyReferralLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    const inputTemp = document.createElement('input')
    inputTemp.value = referralUrl
    document.body.appendChild(inputTemp)
    inputTemp.select()
    document.execCommand('copy')
    document.body.removeChild(inputTemp)
    trackShareClick({ method: 'copied' })
    setIsCopied(true)
  }

  function postFacebook() {
    // eslint-disable-next-line
    ;(window as any).FB.ui(
      {
        method: 'share',
        href: referralUrl,
        quote: text
      },
      (response: Response) => {
        // eslint-disable-next-line no-console
        console.log(response)
      }
    )
    trackShareClick({ method: 'facebook' })
  }

  if (!render) return null

  return (
    <div className="py-16">
      <div className="mx-auto max-w-screen-lg lg:max-w-7xl md:px-8">
        <div className="md:rounded-lg bg-orange-600 md:px-10 px-4 py-8">
          <h2 className="md:text-4xl text-3xl tracking-wide mt-8 mb-10 font-bold text-center text-white">
            Share and get a free lunch
          </h2>
          <div className="grid md:grid-cols-2 md:gap-x-8 gap-y-2">
            <p className="text-lg mb-4 text-white">
              Share this link with your friends and colleagues and you&apos;ll
              get{' '}
              <span className="font-extrabold text-xl">
                {referralBonus.formatted}
              </span>{' '}
              every time someone places their first order (make sure they click
              on your link or you won&apos;t receive credit)! This link is also
              in the email menus we send you, so forward them on and snag your
              credit.
            </p>

            <div>
              <div className="mt-2 bg-gray-50 rounded-md w-full py-3 px-4 shadow flex flex-row justify-between text-gray-800">
                <div
                  className="max-w-[250px] md:max-w-none w-full"
                  style={{
                    overflowWrap: 'break-word'
                  }}
                >
                  {referralUrl}
                </div>
                <div>
                  <Tippy
                    content={
                      <div
                        className="bg-gray-700 px-2 py-1 text-center rounded-md text-white"
                        role="tooltip"
                      >
                        {isCopied ? 'Copied' : 'Copy'}
                      </div>
                    }
                  >
                    <a
                      className="social-sharing-button-copy"
                      href="#"
                      onClick={onCopyReferralLink}
                    >
                      <DocumentDuplicateIcon className="w-6 h-6 text-gray-500" />
                    </a>
                  </Tippy>
                </div>
              </div>

              <div className="mt-4 flex lg:flex-row flex-col gap-2">
                <Button
                  color="secondary"
                  onClick={(event) => {
                    event.preventDefault()
                    // eslint-disable-next-line
                    if ((window as any).FB) {
                      postFacebook()
                    } else {
                      initializeFacebookAPI().then(() => postFacebook())
                    }
                  }}
                  variant="outline"
                  fullWidth
                >
                  <FacebookIcon className="w-6 h-6 mr-2 text-blue-600" />
                  <span>Facebook</span>
                </Button>
                <ButtonLink
                  color="secondary"
                  href={`http://twitter.com/share?text=${encodeURIComponent(
                    text
                  )}&url=${encodeURIComponent(referralUrl)}`}
                  onClick={() => {
                    trackShareClick({ method: 'twitter' })
                  }}
                  target="_blank"
                  variant="outline"
                  fullWidth
                >
                  <TwitterIcon className="w-6 h-6 mr-2 text-blue-600" />
                  <span>Twitter</span>
                </ButtonLink>
                <Button
                  color="secondary"
                  onClick={(event) => {
                    event.preventDefault()
                    navigator.share({
                      title,
                      text,
                      url: referralUrl
                    })
                    trackShareClick({ method: 'share' })
                  }}
                  variant="outline"
                  fullWidth
                >
                  <ShareIcon className="w-6 h-6 text-blue-600 mr-2" />
                  <span>Share</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
